import { useCallback } from 'react'
import { useAuthzContext } from '@omnea-app/fe-authz/AuthzContextProvider'
import { useRequestRecordPermissions, type RequestRecordPermissionsState } from './useRequestRecordPermissions'
import type { RequestRecordPermissions } from '@omnea-app/authz'

/**
 * Folds the permissions-service answer with the pre-FGA rule for one request affordance. On a
 * legacy organisation the legacy rule decides alone; once the organisation is on the permissions
 * service, the verb decides alone — `unavailable` carries an all-false map, so a check that failed
 * withholds the affordance rather than falling back to the broader role rule.
 */
export const requestActionGate = (
  state: RequestRecordPermissionsState,
  verb: (permissions: RequestRecordPermissions) => boolean,
  legacy: () => boolean
): boolean => {
  switch (state.status) {
    case 'legacy':
      return legacy()
    case 'loading':
      return false
    case 'resolved':
    case 'unavailable':
      return verb(state.permissions)
  }
}

/**
 * The record-permissions state folded into one gate call per affordance. Kept as a hook so each
 * surface wires the flag and the fetch identically.
 */
export const useRequestActionGate = (requestId: string) => {
  // The authz context already folds the FGA rollout flag: `legacy` means the organisation is not on
  // the permissions service, so there is no record-permission answer to fetch.
  const authzContext = useAuthzContext()
  const state = useRequestRecordPermissions(requestId, { enabled: authzContext.status !== 'legacy' })
  return useCallback(
    (verb: (permissions: RequestRecordPermissions) => boolean, legacy: () => boolean): boolean =>
      requestActionGate(state, verb, legacy),
    [state]
  )
}
